import { AssetType } from "../../../../services/instruments";
import { PairAssetType } from "../../../../services/tickerRequests";
import {
  countryOptionOf,
  FOREX_REQUEST_EXCHANGES,
  PAIR_ASSET_TYPE_OPTION_OF,
  PAIR_EXCHANGES,
} from "./constants";
import {
  InstrumentEditorValues,
  InstrumentIdentity,
  InstrumentMethod,
  InstrumentSelection,
} from "./types";

const normalizeCode = (value: string) => value.trim().toUpperCase();

export const identityOf = ({
  symbol,
  exchange,
}: InstrumentIdentity): InstrumentIdentity => ({
  symbol: normalizeCode(symbol),
  exchange: normalizeCode(exchange),
});

export const defaultValuesOf = ({
  symbol,
  exchange,
}: InstrumentIdentity): InstrumentEditorValues => {
  const pairAssetType = FOREX_REQUEST_EXCHANGES.includes(exchange)
    ? "FOREX"
    : "CRYPTO";
  return {
    symbol,
    exchange,
    instrument: null,
    listing: null,
    pairAssetType: PAIR_ASSET_TYPE_OPTION_OF[pairAssetType],
    manualAssetType: null,
    nativeCurrency: "",
    name: "",
    country: null,
  };
};

const optionalName = (name: string) => {
  const trimmed = name.trim();
  return trimmed ? { name: trimmed } : {};
};

// Null while the method's own choice is missing, which only a method with no field to validate can reach.
export const toSelection = (
  method: InstrumentMethod,
  values: InstrumentEditorValues,
): InstrumentSelection | null => {
  const country = values.country ? { sourceCountry: values.country.value } : {};
  const { symbol, exchange } = identityOf(values);
  const nativeCurrency = normalizeCode(values.nativeCurrency);

  switch (method) {
    case "EXISTING": {
      if (!values.instrument) {
        return null;
      }
      const { instrument } = values.instrument;
      const chosenCountry = values.country?.value ?? null;
      // A stored code outside COUNTRY_OPTIONS prefills empty, so an untouched field must not send that as a clear.
      const prefilledCountry =
        countryOptionOf(instrument.sourceCountry)?.value ?? null;
      return {
        method,
        instrumentId: instrument.id,
        ...(chosenCountry === prefilledCountry
          ? {}
          : { sourceCountry: chosenCountry }),
      };
    }
    case "LISTED":
      return values.listing
        ? {
            method,
            symbol,
            exchange,
            yahooSymbol: values.listing.value,
            ...country,
          }
        : null;
    case "PAIR": {
      const assetType = values.pairAssetType.value as PairAssetType;
      return {
        method,
        symbol,
        exchange: PAIR_EXCHANGES[assetType],
        assetType,
        nativeCurrency,
        ...optionalName(values.name),
      };
    }
    case "MANUAL":
      return values.manualAssetType
        ? {
            method,
            symbol,
            exchange,
            assetType: values.manualAssetType.value as AssetType,
            nativeCurrency,
            ...optionalName(values.name),
            ...country,
          }
        : null;
  }
};
